import React, {useEffect, useState, useContext} from 'react';
import {Card, Form} from 'antd';
import {CheckRounded, InfoRounded, MenuBookRounded, PushPinRounded} from '@mui/icons-material';
import CircularLoader from '@common/CircularLoader';
import MathJaxHTMLContent from '@common/MathJaxHTMLContent';
import lessonResourceService from '../../lessonResourceService';
import {BrandContext} from '@context/BrandContext';

/**
 * Step 3: Confirm Selection
 * Refactored to fetch and display real resources based on selected concepts and resource types.
 * Users can toggle individual resources on/off. Selections are stored in form state
 * and saved to backend after lesson plan creation.
 */
const ConfirmSelection = ({ selectedResources = {}, selectedConcepts = {}, onSelectionsChange }) => {
    const form = Form.useFormInstance();
    const { brandConfig } = useContext(BrandContext);
    const [loading, setLoading] = useState(true);
    const [availableResources, setAvailableResources] = useState({});
    const [selections, setSelections] = useState({});

    // Extract first selected concept ID
    const conceptId = Object.keys(selectedConcepts)
        .filter(id => selectedConcepts[id])
        .map(id => parseInt(id))[0];

    // Fetch real resources based on selected concept and resource types
    useEffect(() => {
        if (!conceptId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Build list of fetch promises based on selected resource types
        const fetchPromises = [];
        const resourceKeys = [];

        if (selectedResources.textbookContent) {
            fetchPromises.push(lessonResourceService.getTutorials(conceptId, 'LESSON', null, 0, 100));
            resourceKeys.push('textbookContent');
        }

        if (selectedResources.examples || selectedResources.exercise) {
            fetchPromises.push(lessonResourceService.getTutorials(conceptId, 'GENERAL_CONTENT', ['EXAMPLE'], 0, 100));
            resourceKeys.push('examples');
        }

        if (selectedResources.quiz || selectedResources.exercise) {
            fetchPromises.push(lessonResourceService.getQuestionClusters(conceptId, ['EXERCISE'], null, 0, 100));
            resourceKeys.push('exercises');
        }

        if (selectedResources.quizQuestions) {
            fetchPromises.push(lessonResourceService.getQuestionClusters(conceptId, ['QUICK_QUIZ'], null, 0, 100));
            resourceKeys.push('quizzes');
        }

        if (selectedResources.walkthroughs) {
            fetchPromises.push(lessonResourceService.getWalkthroughQuestions(conceptId, 0, 100));
            resourceKeys.push('walkthroughs');
        }

        if (selectedResources.videos) {
            fetchPromises.push(lessonResourceService.getMediaResources(conceptId, 'VIDEO', 0, 100));
            resourceKeys.push('videos');
        }

        if (selectedResources.worksheets) {
            fetchPromises.push(lessonResourceService.getMediaResources(conceptId, 'WORKSHEET', 0, 100));
            resourceKeys.push('worksheets');
        }

        if (selectedResources.extras) {
            fetchPromises.push(
                lessonResourceService.getMediaResources(conceptId, 'DESMOS', 0, 50),
                lessonResourceService.getMediaResources(conceptId, 'WIDGET', 0, 50),
                lessonResourceService.getMediaResources(conceptId, 'SCORCHER', 0, 50)
            );
            resourceKeys.push('desmos', 'widget', 'scorcher');
        }

        if (fetchPromises.length === 0) {
            setLoading(false);
            return;
        }

        Promise.all(fetchPromises)
            .then(responses => {
                const resources = {};
                const initialSelections = {};

                responses.forEach((response, index) => {
                    const key = resourceKeys[index];
                    const items = response.content || [];

                    // Map to our resource format
                    let mappedItems = [];
                    if (key === 'textbookContent' || key === 'examples') {
                        mappedItems = items.map(t => ({
                            resourceType: 'TUTORIAL',
                            resourceId: t.tutorialId,
                            title: t.tutorialName,
                            code: t.tutorialNumber ? `${key === 'examples' ? 'Example' : 'Tutorial'} ${t.tutorialNumber}` : '',
                        }));
                    } else if (key === 'exercises' || key === 'quizzes') {
                        mappedItems = items.map(c => ({
                            resourceType: 'QUESTION_CLUSTER',
                            resourceId: c.questionClusterId,
                            title: c.name,
                            questions: c.questionCount,
                        }));
                    } else if (key === 'walkthroughs') {
                        mappedItems = items.map(q => ({
                            resourceType: 'QUESTION',
                            resourceId: q.questionId,
                            title: q.instructions || 'Walkthrough',
                            description: q.contentSummary || '',
                        }));
                    } else if (key === 'videos' || key === 'worksheets' || key === 'desmos' || key === 'widget' || key === 'scorcher') {
                        mappedItems = items.map(m => ({
                            resourceType: 'MEDIA',
                            resourceId: m.mediaId,
                            title: m.mediaName,
                            type: key,
                        }));
                    }

                    resources[key] = mappedItems;

                    // Auto-select top 3 items from each category
                    mappedItems.slice(0, 3).forEach(item => {
                        const itemKey = `${item.resourceType}:${item.resourceId}`;
                        initialSelections[itemKey] = true;
                    });
                });

                setAvailableResources(resources);
                setSelections(initialSelections);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error loading resources:', error);
                setLoading(false);
            });
    }, [conceptId, selectedResources]);

    // Update form state and parent wizard state whenever selections change
    useEffect(() => {
        form.setFieldValue('reviewSelections', selections);
        // Also notify parent wizard so it can store in state
        if (onSelectionsChange) {
            onSelectionsChange(selections);
        }
    }, [selections, form, onSelectionsChange]);

    const toggleItem = (resourceType, resourceId) => {
        const key = `${resourceType}:${resourceId}`;
        setSelections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Calculate total selected
    const totalSelected = Object.values(selections).filter(Boolean).length;

    // Render a resource checkbox
    const renderResourceRow = (resource, sectionKey) => {
        const key = `${resource.resourceType}:${resource.resourceId}`;
        const checked = selections[key] || false;

        return (
            <div key={key} style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleItem(resource.resourceType, resource.resourceId)}
                    />
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {resource.code && (
                            <span style={{ color: '#999' }}>{resource.code}</span>
                        )}
                        <MathJaxHTMLContent
                            ElementTag="span"
                            html={resource.title}
                            showLoader={false}
                            style={{ flex: 1 }}
                        />
                        {resource.questions && (
                            <span style={{ color: '#999', whiteSpace: 'nowrap' }}>
                                — {resource.questions} questions
                            </span>
                        )}
                    </div>
                </label>
            </div>
        );
    };

    // Group extras together
    const extrasItems = [
        ...(availableResources.desmos || []),
        ...(availableResources.widget || []),
        ...(availableResources.scorcher || []),
    ];

    if (loading) {
        return (
            <div style={{ padding: '40px', textAlign: 'center' }}>
                <CircularLoader />
                <p>Loading available resources...</p>
            </div>
        );
    }

    if (!conceptId) {
        return (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                <p>No concept selected. Please go back to Step 1.</p>
            </div>
        );
    }

    return (
        <div className="ch-lesson-planner-review">
            <div>
                <h2>Review and select resources</h2>
                <p className="ch-lesson-planner-step-subtitle">
                    Select the specific resources you want in your lesson plan. Top picks are pre-selected.
                </p>
            </div>

            <Card className="ch-lesson-planner-review-summary">
                <div className="ch-lesson-planner-review-summary-inner">
                    <div className="ch-lesson-planner-review-summary-icon" aria-hidden="true">
                        <CheckRounded fontSize="small" />
                    </div>
                    <div>
                        <div className="ch-lesson-planner-review-summary-count">
                            {totalSelected} resources selected for your lesson plan
                        </div>
                        <div className="ch-lesson-planner-review-summary-sub">
                            Check or uncheck resources below to customize your lesson.
                        </div>
                    </div>
                </div>
            </Card>

            {/* Always Included Sections */}
            <div style={{ padding: '16px', background: '#f5f5f5', borderRadius: '4px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <PushPinRounded style={{ fontSize: '18px', color: '#666' }} />
                    <strong>Lesson Summary</strong>
                    <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#999' }}>Always included</span>
                </div>
                <p style={{ margin: 0, color: '#666' }}>
                    We'll generate a summary of key takeaways based on your selected content.
                </p>
            </div>

            <div style={{ padding: '16px', background: '#f5f5f5', borderRadius: '4px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <InfoRounded style={{ fontSize: '18px', color: '#666' }} />
                    <strong>Lesson Overview</strong>
                    <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#999' }}>Always included</span>
                </div>
                <p style={{ margin: 0, color: '#666' }}>Lesson overview and metadata are always included.</p>
            </div>

            {/* Textbook Content */}
            {availableResources.textbookContent?.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ marginBottom: '8px' }}>
                        <MenuBookRounded style={{ fontSize: '20px', verticalAlign: 'middle', marginRight: '8px' }} />
                        Textbook Content
                    </h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.textbookContent.map(r => renderResourceRow(r, 'textbookContent'))}
                    </div>
                </div>
            )}

            {/* Examples */}
            {availableResources.examples?.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ marginBottom: '8px' }}>Examples</h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.examples.map(r => renderResourceRow(r, 'examples'))}
                    </div>
                </div>
            )}

            {/* Exercises */}
            {availableResources.exercises?.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ marginBottom: '8px' }}>{brandConfig?.exercisesName || 'Exercises'}</h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.exercises.map(r => renderResourceRow(r, 'quiz'))}
                    </div>
                </div>
            )}

            {/* Quizzes */}
            {availableResources.quizzes?.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ marginBottom: '8px' }}>Quizzes</h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.quizzes.map(r => renderResourceRow(r, 'quizQuestions'))}
                    </div>
                </div>
            )}

            {/* Walkthroughs */}
            {availableResources.walkthroughs?.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ marginBottom: '8px' }}>{brandConfig?.walkthroughsName || 'Walkthroughs'}</h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.walkthroughs.map(r => renderResourceRow(r, 'walkthroughs'))}
                    </div>
                </div>
            )}

            {/* Videos */}
            {availableResources.videos?.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ marginBottom: '8px' }}>{brandConfig?.videosName || 'Videos'}</h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.videos.map(r => renderResourceRow(r, 'videos'))}
                    </div>
                </div>
            )}

            {/* Worksheets */}
            {availableResources.worksheets?.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ marginBottom: '8px' }}>{brandConfig?.worksheetsName || 'Worksheets'}</h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {availableResources.worksheets.map(r => renderResourceRow(r, 'worksheets'))}
                    </div>
                </div>
            )}

            {/* Extras */}
            {extrasItems.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ marginBottom: '8px' }}>
                        {[
                            brandConfig?.scorcherName || 'Scorcher',
                            brandConfig?.desmosName || 'Desmos',
                            brandConfig?.widgetsName || 'Widgets'
                        ].filter(Boolean).join(', ').replace(/, ([^,]*)$/, ' and $1')}
                    </h3>
                    <div style={{ background: '#fff', border: '1px solid #d9d9d9', borderRadius: '4px' }}>
                        {extrasItems.map(r => renderResourceRow(r, 'extras'))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConfirmSelection;
